<div class="mb-6 flex items-center justify-between px-6 pt-8">
  <h4 class="typo-h4-medium">{{ 'health_history_notes_section_title' | translate }}</h4>
</div>

@if (!isEdited()) {
  <div class="flex max-h-full grow overflow-auto">
    @if (patientNotesLoading()) {
      <ng-lottie class="mx-5 flex h-4 w-4 text-primary-blue" currentcolor [options]="loadingLottieOptions"></ng-lottie>
    } @else {
      <h4
        class="typo-h5-book mx-5 mb-5 flex-grow whitespace-pre-line rounded-2xl p-2 text-text-secondary hover:cursor-text hover:bg-background-light-grey"
        (click)="onPatientNotesClick($event)"
      >
        {{ patientNotes() || ('health_history_notes_section_placeholder' | translate) }}
      </h4>
    }
  </div>
} @else {
  <div
    class="text-container mx-1.5 mb-2 flex flex-grow flex-col overflow-hidden rounded-3xl border-x-8 border-b-8 border-t-8 border-background-light-grey bg-background-light-grey text-text-secondary transition-[margin] duration-300"
  >
    @if (currentHistoryElementSignal(); as currentHistoryElement) {
      <div class="flex items-center justify-between bg-background-light-grey pb-2">
        <div class="flex flex-grow flex-wrap items-center justify-between gap-3">
          <div
            class="relative order-last flex h-10 items-center justify-center gap-2 overflow-hidden rounded-2xl border border-states-purple/20 bg-other-toolbar-purple p-1 max-[1176px]:flex-grow"
            [class]="transcriptionStatus() === 'recording' ? 'min-[1176px]:order-none' : 'min-[1092px]:order-none'"
          >
            <button
              type="button"
              class="group z-10 flex h-full items-center justify-center gap-2 rounded-xl bg-background-white px-3.5 py-1 transition-colors active:border-text-white disabled:bg-other-voice-control disabled:opacity-40 max-[1176px]:flex-grow"
              (click)="enhanceNote(EnhancePatientNotesPromptType.REWRITE)"
              [disabled]="isGeneratingSignal() || transcriptionStatus() !== 'idle'"
            >
              @if (isGeneratingSignal()) {
                <ng-lottie class="h-4 w-4" currentcolor class="text-states-purple" [options]="enhanceLottieOptions"></ng-lottie>
              } @else {
                <svg-icon class="h-8 w-8 shrink-0 text-states-purple" key="px-16-enhance-2" [fontSize]="16"></svg-icon>
              }
              <span class="typo-h5-semibold text-states-purple">{{ 'health_history_notes_section_rewrite_enhance' | translate }}</span>
            </button>
            <button
              type="button"
              class="flex items-center justify-center gap-1 self-stretch overflow-hidden rounded-xl bg-background-white px-4 py-1 text-states-purple transition-colors disabled:bg-other-voice-control disabled:opacity-40 max-[1176px]:flex-grow"
              (click)="enhanceNote(EnhancePatientNotesPromptType.CONCISE)"
              [disabled]="isGeneratingSignal() || transcriptionStatus() !== 'idle'"
            >
              <svg-icon class="h-8 w-8 shrink-0" key="px-16-concise" [fontSize]="16"></svg-icon>
              <span class="typo-h5-semibold">{{ 'health_history_notes_section_concise_enhance' | translate }}</span>
            </button>
            <button
              type="button"
              class="flex items-center justify-center gap-1 self-stretch overflow-hidden rounded-xl bg-background-white px-4 py-1 text-states-purple transition-colors disabled:bg-other-voice-control disabled:opacity-40 max-[1176px]:flex-grow"
              (click)="enhanceNote(EnhancePatientNotesPromptType.BULLETS)"
              [disabled]="isGeneratingSignal() || transcriptionStatus() !== 'idle'"
            >
              <svg-icon class="h-8 w-8 shrink-0" key="px-20-bullet-list" [fontSize]="16"></svg-icon>
              <span class="typo-h5-semibold">{{ 'health_history_notes_section_bullets_enhance' | translate }}</span>
            </button>
          </div>
          <div class="flex h-8 items-center gap-2 max-[1176px]:flex-grow">
            <div class="flex items-center gap-1 max-[1176px]:mr-auto">
              <button
                type="button"
                class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-xl text-primary-blue active:bg-background-disabled disabled:text-text-extra-light"
                [libUiTooltip]="undoTooltip"
                [tooltipPosition]="'bottom'"
                [disabled]="undoDisabledSignal() || transcriptionStatus() !== 'idle'"
                (click)="undo()"
              >
                <svg-icon key="px-24-undo" [style.width]="'20px'" [style.height]="'20px'"></svg-icon>
              </button>
              <ng-template #undoTooltip>
                <div class="px-3 py-2">
                  <div class="typo-h7-light">{{ 'notes_undo_button' | translate }}</div>
                </div>
              </ng-template>
              <button
                type="button"
                class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-xl text-primary-blue active:bg-background-disabled disabled:text-text-extra-light"
                [libUiTooltip]="redoTooltip"
                [tooltipPosition]="'bottom'"
                [disabled]="redoDisabledSignal() || transcriptionStatus() !== 'idle'"
                (click)="redo()"
              >
                <svg-icon key="px-24-redo" [style.width]="'20px'" [style.height]="'20px'"></svg-icon>
              </button>
              <ng-template #redoTooltip>
                <div class="px-3 py-2">
                  <div class="typo-h7-light">{{ 'notes_redo_button' | translate }}</div>
                </div>
              </ng-template>
            </div>
          </div>
        </div>
      </div>
    }
    <div class="flex min-h-[112px] flex-grow flex-col justify-between overflow-y-auto rounded-3xl bg-background-white p-4">
      <lib-voice-typing-text-field
        #voiceTypingTextField
        class="max-h-full overflow-auto"
        [value]="(controlledNote$ | async) ?? ''"
        [lastFocusedCursorOffset]="lastFocusedCursorOffsetSignal()"
        (lastFocusedCursorOffsetChange)="updateLastFocusedCursorOffset($event)"
        [transcriptionFormControl]="transcription"
        [transcriptionStatus$]="transcriptionStatus$"
        [isRecording]="transcriptionStatus() === 'recording'"
        [isRecordingLoading]="transcriptionStatus() === 'loading'"
        [placeholder]="'health_history_notes_section_placeholder_edit' | translate"
        (manualTypeKeyUp)="onManualTypeKeyUp($event)"
        (recordingLoading)="onRecordingLoading($event)"
        [lineBreakOnShiftEnter]="true"
        [contextMenuHeaderConfig]="contextMenuHeaderConfig()"
        (generateText)="onGenerateTextFromContextMenu($event)"
      ></lib-voice-typing-text-field>

      <div class="mt-4 flex items-center justify-between gap-2">
        <div class="flex gap-2">
          <div class="relative">
            <button
              class="transition-[min-width, background-color] group flex h-full min-h-[32px] min-w-[32px] items-center justify-center rounded-xl bg-background-white p-1 text-states-purple duration-200 ease-in-out"
              (click)="onDictationClick()"
              [ngClass]="{
                'min-w-[169px] bg-states-purple shadow-[0px_4px_24px_0px] shadow-states-purple/30': transcriptionStatus() === 'recording'
              }"
              [disabled]="isGeneratingSignal()"
            >
              @switch (transcriptionStatus()) {
                @case ('idle') {
                  <svg-icon
                    [libUiTooltip]="startVoiceTypingTooltip"
                    [tooltipPosition]="'top'"
                    class=""
                    currentColor
                    key="px-24-voice-v-2"
                    size="md"
                  ></svg-icon>
                }
                @case ('loading') {
                  <ng-lottie class="h-4 w-4" currentcolor [options]="loadingLottieOptions"></ng-lottie>
                }
                @case ('recording') {
                  <svg-icon
                    [libUiTooltip]="stopVoiceTypingTooltip"
                    [tooltipPosition]="'top'"
                    key="px-40-stop"
                    class="text-text-white"
                    size="md"
                  ></svg-icon>
                }
              }
            </button>
          </div>

          @if (transcriptionStatus() === 'recording') {
            <div class="flex h-8 items-center gap-2">
              <ffd-audio-input-dropdown
                [activeDeviceId]="activeDeviceId()"
                (selectDevice)="onSelectDevice($event)"
              ></ffd-audio-input-dropdown>
            </div>
          }
        </div>

        <lib-note-saving-indicator
          [isLoading]="isPatientNotesSavingInProgress()"
          [isSaved]="isPatientNotesSaved()"
        ></lib-note-saving-indicator>
      </div>
    </div>
  </div>
}

<ng-template #startVoiceTypingTooltip>
  <div class="text-h7">Voice Typing</div>
</ng-template>
<ng-template #stopVoiceTypingTooltip>
  <div class="text-h7">Stop Voice Typing</div>
</ng-template>
